/* Smoke and fire emitter configs for the masthead overlay.

   Coordinates are pinned in briefs/particle-emitters.md as pixel positions
   in masthead.jpg's 1600×640 frame. tsparticles emitter `position` takes
   percentages (0–100), so we convert here. The masthead's container has
   the exact 2.5:1 aspect ratio of the image, so percentages map to
   rendered space exactly regardless of viewport scale.

   Neither layer uses tsparticles emitters anymore — spawning happens in
   per-frame loops (breathing.ts for smoke; fireBreath.ts for the
   celebration fire-breath) so we can drive intensity envelopes
   (sinusoidal breathing, end-of-breath tail-off) and write initial
   velocity directly. The particle *templates* below
   (opacity/size/life/gravity/etc.) still ship in each *_OPTIONS'
   particles section so addParticle inherits them. */

import type { ISourceOptions } from "@tsparticles/engine";
import { PUFF_SPRITE_SIZE, getTintedPuffSpriteUrl } from "./sprites";

const W = 1600;
const H = 640;
const pct = (px: number, total: number) => (px / total) * 100;

/** Reference canvas width (in CSS px) at which the particle constants —
    velocities, gravity, particle size, size-grow speed — are calibrated.
    Matches `--masthead-max-width`, the largest the masthead is ever
    rendered. breathing.ts and fireBreath.ts scale every distance-per-time
    quantity by `currentCanvasWidth / MASTHEAD_REFERENCE_WIDTH` at spawn
    time so a small canvas produces a proportionally smaller plume —
    instead of the same absolute-px plume that, on a small canvas,
    visually overruns the dragon. */
export const MASTHEAD_REFERENCE_WIDTH = 720;

// Light, near-white smoke with subtle tonal variation. Pre-baked into
// each puff sprite (since replaceColor is a no-op in this build), so
// pushing the palette lighter directly brightens the rendered smoke.
const SMOKE_COLORS = ["#ffffff", "#f9fafb", "#f3f4f6", "#e5e7eb"];

// =====================================================================
// FIRE TUNING KNOBS
// =====================================================================
// All the constants you'll want to tweak to dial in the dragon's
// fire-breath are gathered here. WIN_OPTIONS' particle template + the
// fireBreath.ts spawner read from these — no magic numbers buried in
// nested objects.
// =====================================================================

/** Fire palette. One PNG per colour is pre-tinted at runtime; particles
    pick one at spawn. With additive blending (composite:"lighter") each
    particle adds `colour × alpha` onto the canvas, so overlap of a few
    deep-orange puffs naturally summates to bright yellow → white in the
    densest regions — the "hot core" effect. Skewing the palette toward
    deep orange/red gives single puffs colour weight; bright yellow at
    the head of the array still appears occasionally for variety. */
const FIRE_COLORS = ["#fde047", "#fbbf24", "#f97316", "#ea580c", "#dc2626"];

/** Per-particle peak opacity. With additive (`lighter`) blending on the
    canvas (see WIN_OPTIONS.backgroundMask), each particle's contribution
    is approximately `colour × alpha` summed onto whatever's already
    drawn. Standard fire-effect BCP: keep single particles modest
    (0.2–0.4) so overlap can build dynamic range and saturate to white-
    hot only at the densest cores. Higher values blow out the canvas
    almost immediately and lose all colour information. */
const FIRE_OPACITY_MAX = 0.5;
/** Per-particle floor opacity. Keep at 0 — particles destroy themselves
    when opacity hits this, so anything > 0 cuts visible lifetime short. */
const FIRE_OPACITY_MIN = 0;
/** Opacity-fade speed in opacity-units/sec. Higher = faster fade =
    shorter visible projection. Lower = particles travel further before
    vanishing. Should be ≲ FIRE_OPACITY_MAX / FIRE_LIFE_S to let fade
    complete inside the lifetime cap. */
const FIRE_FADE_SPEED = 0.7;

/** Emission rate. Particles per second per mouth ≈ quantity / delay,
    consumed by fireBreath.ts as the peak (intensity = 1) rate. With
    additive blending, denser emission = hotter core. Bump quantity for
    more saturation at the mouth; raise delay to thin out the cone. */
export const FIRE_EMIT_DELAY_S = 0.015;
export const FIRE_EMIT_QUANTITY = 40;

/** Initial particle speed range (tsparticles units @
    MASTHEAD_REFERENCE_WIDTH). fireBreath.ts picks a random value in
    this range per particle, then scales by current intensity (so
    tail-off particles project less) AND by the canvas-relative
    scale factor (so a 360-px-wide masthead's plume doesn't overrun
    the dragon while a 720-px-wide masthead's plume looks right).
    Higher = particles travel further from the mouth in a given
    lifetime. */
export const FIRE_SPEED_MIN = 10;
export const FIRE_SPEED_MAX = 12;

/** Cone half-angle in degrees. Lower = tighter jet (more overlap, hotter
    core). Higher = wider spread. */
export const FIRE_CONE_DEG = 30;

/** Length of the smooth tail-off at the end of the fire breath. Spawn
    rate AND per-particle initial speed both scale by a cosine ease-out
    over this window, so the breath thins and weakens before going
    silent (mirroring the smoke-breathing exhale's descending limb).
    Sits inside the FIRE_MS duration — i.e., FIRE_MS - FIRE_TAIL_OFF_MS
    of full-strength breath, then FIRE_TAIL_OFF_MS of taper. */
export const FIRE_TAIL_OFF_MS = 400;

/** Particle size range in CSS pixels (calibrated at
    MASTHEAD_REFERENCE_WIDTH). Particles spawn at MIN and grow toward
    MAX as they age and travel downstream. CRITICAL: keep MIN ≥ ~1 px.
    Sprites smaller than 1 px are sub-pixel — the canvas renders a
    128×128 PNG into a destination smaller than 1 px and the result
    contributes essentially zero alpha. Sub-pixel particles are
    invisible regardless of opacity, so a tiny MIN produces NO fire
    until particles age into a visible size. There's also a fundamental
    tension: density is highest at the mouth (where the hot core would
    naturally form) but particles there are youngest = smallest under
    age-driven growth, so a small MIN inherently dims the brightest
    region. To make the mouth read as a hot pinpoint, MIN must be small
    but visible (1–3 px) — true sub-pixel "invisible at exact origin"
    isn't recoverable with size animation alone. */
export const FIRE_SIZE_MIN = 5;
export const FIRE_SIZE_MAX = 50;
/** Size growth rate in size-units/sec (calibrated at
    MASTHEAD_REFERENCE_WIDTH). Internally tsparticles converts via
    velocity = SPEED / 100 per delta-tick (≈ per frame at 60 fps), so
    SPEED = 22 ⇒ ~13.2 px/sec growth. Over the effective lifetime
    (~0.5 s, capped by opacity destroy) that's ~6.6 px of growth, so a
    particle starting at MIN=2 ends at ~9 px — well inside MAX=20. */
export const FIRE_SIZE_GROW_SPEED = 50;

/** Downward gravity acceleration (px/sec² @ MASTHEAD_REFERENCE_WIDTH).
    Higher = stronger downward arc. The fire breath is gravity-affected
    so streams droop over distance, which reads as flame falling. */
export const FIRE_GRAVITY = 4;

/** Per-particle lifetime cap in seconds. Combined with FIRE_SPEED_*
    sets max travel distance from the mouth. */
const FIRE_LIFE_S = 1;

// =====================================================================
// END FIRE TUNING KNOBS
// =====================================================================

/** Soft Gaussian puff. The current `@tsparticles/shape-image` build
   ignores `replaceColor`, so we pre-tint one PNG per palette colour and
   hand the engine an `image: [...]` array — particles pick a sprite at
   spawn, and the gradient is in the right colour from frame zero. */
const SMOKE_SHAPE = {
  type: "image",
  options: {
    image: SMOKE_COLORS.map((hex) => ({
      src: getTintedPuffSpriteUrl(hex),
      width: PUFF_SPRITE_SIZE,
      height: PUFF_SPRITE_SIZE,
    })),
  },
} as const;

const FIRE_SHAPE = {
  type: "image",
  options: {
    image: FIRE_COLORS.map((hex) => ({
      src: getTintedPuffSpriteUrl(hex),
      width: PUFF_SPRITE_SIZE,
      height: PUFF_SPRITE_SIZE,
    })),
  },
} as const;

/** Dragon mouths in image-percentage coords + outward thrust direction
    (screen coords: 0° = right, 90° = down). Consumed by fireBreath.ts
    to pick spawn positions and cone-randomized angles. */
export const MOUTHS: ReadonlyArray<{
  xPct: number;
  yPct: number;
  /** Centerline of this mouth's fire cone. */
  dirDeg: number;
}> = [
  // Left dragon — exhales toward the bottom-right.
  { xPct: pct(232, W), yPct: pct(303, H), dirDeg: 45 },
  // Right dragon — exhales toward the bottom-left.
  { xPct: pct(1383, W), yPct: pct(285, H), dirDeg: 135 },
];

const baseShared = {
  fullScreen: { enable: false },
  background: { color: "transparent" },
  detectRetina: true,
  fpsLimit: 60,
  particles: {
    number: { value: 0 },
    move: { enable: true, outModes: { default: "destroy" as const } },
  },
} satisfies Partial<ISourceOptions>;

// =====================================================================
// SMOKE TUNING KNOBS (calibrated at MASTHEAD_REFERENCE_WIDTH)
// =====================================================================

/** Smoke particle initial size range (CSS px @ reference width). */
export const SMOKE_SIZE_MIN = 7;
export const SMOKE_SIZE_MAX = 16;
/** Smoke size growth rate (size-units/sec @ reference width). */
export const SMOKE_SIZE_GROW_SPEED = 3;
/** Smoke buoyancy — negative = upward (px/sec² @ reference width). */
export const SMOKE_GRAVITY = -1.5;

/** SMOKE_OPTIONS deliberately ships with NO emitters — breathing.ts owns
   spawning. The particles section here is the template addParticle()
   inherits: shape, palette, fade animation, gravity buoyancy, soft-kill,
   life cap. The breathing module overrides initial velocity per-particle
   so move.speed:0 below is the steady-state baseline.

   Distance-per-time quantities (size, size-grow speed, gravity) are
   calibrated at MASTHEAD_REFERENCE_WIDTH; breathing.ts scales them
   per-particle via addParticle()'s override arg so a smaller canvas
   produces a proportionally smaller plume. The values here are the
   reference-size defaults — they apply unscaled if the override path
   ever fails. */
export const SMOKE_OPTIONS: ISourceOptions = {
  ...baseShared,
  particles: {
    number: { value: 0 },
    shape: SMOKE_SHAPE,
    opacity: {
      // sync:true is critical — see commit 7cde5d2. With sync:false the
      // configured fade velocity gets multiplied by Math.random(), so a
      // high percentage of particles end up with fade times longer than
      // the lifetime cap and snap-kill mid-fade.
      value: { min: 0, max: 0.1 },
      animation: {
        enable: true,
        speed: { min: 0.05, max: 0.1 },
        sync: true,
        startValue: "max",
        destroy: "min",
      },
    },
    size: {
      value: { min: SMOKE_SIZE_MIN, max: SMOKE_SIZE_MAX },
      animation: {
        enable: true,
        speed: SMOKE_SIZE_GROW_SPEED,
        sync: true,
        startValue: "min",
        destroy: "none",
      },
    },
    move: {
      enable: true,
      // `move.speed` is a per-frame multiplier on particle.velocity AND
      // a gate on gravity (`if (moveSpeed)` early-return in move-base).
      // Must stay non-zero or our breathing-loop velocity overrides
      // multiply to zero and nothing moves. The thrust direction lives
      // in the per-particle velocity vector; this is just the global
      // scale.
      direction: "none",
      speed: 1,
      random: false,
      straight: true,
      drift: { min: -0.15, max: 0.15 },
      // Decay damps the initial breathing thrust over ~1 second so
      // gravity buoyancy can take over — without this, a peak-of-cycle
      // particle keeps thrusting downward for its whole life because
      // the upward gravity is too gentle to cancel a strong vy.
      decay: 0.02,
      // Stronger buoyancy than the old static config so that, once
      // decay has eaten the thrust, particles rise visibly.
      gravity: { enable: true, acceleration: SMOKE_GRAVITY },
      outModes: { default: "none" },
    },
    life: {
      duration: { value: { min: 3.8, max: 12 }, sync: false },
      count: 1,
    },
  },
  emitters: [],
};

export const IDLE_OPTIONS: ISourceOptions = {
  ...baseShared,
  emitters: [],
};

/** Wall-clock delay between the celebration trigger and the masthead
    swapping from smoke to fire. The win confetti (fired independently
    by BoardEffects on the same trigger) launches from the bottom
    corners during this lead — giving it solo screen time so the fire
    reads as the finale rather than competing visual noise. Smoke keeps
    running until the swap, so the masthead never goes silent. */
export const WIN_SEQUENCE_FIRE_LEAD_MS = 2000;
/** Total wall-clock duration of the win sequence (breath-in + fire breath),
   used by callers to schedule the smoke resumption. */
export const WIN_SEQUENCE_BREATH_IN_MS = 500;
export const WIN_SEQUENCE_FIRE_MS = 2500;
export const WIN_SEQUENCE_TOTAL_MS =
  WIN_SEQUENCE_BREATH_IN_MS + WIN_SEQUENCE_FIRE_MS;

/** WIN_OPTIONS deliberately ships with NO emitters — fireBreath.ts owns
   spawning. The particles section here is the template addParticle()
   inherits: shape, palette, opacity-fade, size growth, downward
   gravity, life cap. The fireBreath module overrides initial velocity
   per-particle so move.speed:1 below is just the (non-zero) multiplier
   — see SMOKE_OPTIONS' move.speed comment for why it can't be 0. */
export const WIN_OPTIONS: ISourceOptions = {
  ...baseShared,
  // ADDITIVE BLENDING (the load-bearing line for the hot-core effect).
  // tsparticles' backgroundMask, when enabled, sets
  // `globalCompositeOperation = composite` per particle in
  // CanvasUtils.drawParticle (engine source: cjs/Utils/CanvasUtils.js,
  // line ~50). With composite:"lighter" each particle's RGB is *added*
  // to whatever's already on the canvas instead of alpha-blending — so
  // 5 deep-orange puffs at the same point sum past the orange ceiling
  // toward yellow, 10+ saturate to white. That's the physical model of
  // real flames (light is additive) and it's the BCP for fire effects
  // in 2D canvas particle systems.
  //
  // The `cover` field is required by the type but with opacity:0 it
  // doesn't actually paint anything; we only want the composite-mode
  // side effect, not the masking behaviour the feature was designed for.
  backgroundMask: {
    enable: true,
    composite: "lighter",
    cover: { color: { value: "#000000" }, opacity: 0 },
  },
  particles: {
    number: { value: 0 },
    shape: FIRE_SHAPE,
    opacity: {
      value: { min: FIRE_OPACITY_MIN, max: FIRE_OPACITY_MAX },
      animation: {
        enable: true,
        speed: FIRE_FADE_SPEED,
        sync: true,
        startValue: "max",
        destroy: "min",
      },
    },
    size: {
      value: { min: FIRE_SIZE_MIN, max: FIRE_SIZE_MAX },
      animation: {
        enable: true,
        speed: FIRE_SIZE_GROW_SPEED,
        // sync:true is critical here. With sync:false, tsparticles'
        // SizeUpdater.init does `velocity *= getRandom()` (random in
        // [0,1)), so each particle gets a random fraction of the
        // configured grow rate — many particles barely grow at all
        // and never reach visible size before opacity destroys them.
        // sync:true gives every particle the same predictable rate.
        sync: true,
        // startValue:"min" + destroy:"none" → particles spawn at
        // FIRE_SIZE_MIN (small at the mouth) and grow outward toward
        // FIRE_SIZE_MAX as they age. Lifetime is governed by opacity
        // destroy ≈ FIRE_OPACITY_MAX / FIRE_FADE_SPEED, not life.
        startValue: "min",
        destroy: "none",
      },
    },
    move: {
      enable: true,
      // direction:"none" + speed:1 + per-particle velocity write in
      // fireBreath.ts is the same pattern SMOKE_OPTIONS uses with
      // breathing.ts. move.speed must stay > 0 so the engine doesn't
      // multiply velocity to zero and gate gravity off.
      direction: "none",
      speed: 1,
      random: false,
      straight: true,
      gravity: { enable: true, acceleration: FIRE_GRAVITY },
      outModes: { default: "destroy" },
    },
    life: { duration: { value: FIRE_LIFE_S, sync: false }, count: 1 },
  },
  emitters: [],
};
